import React, { memo } from 'react';

import './FormField.scss';
import RadioButton from '../RadioButton';
import Currency from '../Currency';
import Select from '../Select';
import DatePicker from '../DatePicker';
import TimePicker from '../TimePicker';
import ErrorLabel from '../ErrorLabel';
import MaskedInput from '../MaskedInput/MaskedInput';
import CircleLoader from '../Loader/CircleLoader';
import bem from '../../utils/bem';

const renderControl = (
  id,
  name,
  type,
  onChange,
  value,
  options,
  placeholder,
  disabled,
  format,
  mask,
  onBlur,
  min,
  isLoading,
  pattern = null,
  url,
  orientation,
  inlineTitle
) => {
  if (isLoading && !value) {
    return (
      <div className="form-field__loading">
        <CircleLoader />
      </div>
    );
  }
  switch (type) {
    case 'masked':
      return (
        <MaskedInput
          className="form-field__input"
          id={id}
          name={name}
          type={type}
          onChange={onChange}
          value={value}
          placeholder={placeholder}
          format={format}
          mask={mask}
          onBlur={onBlur}
        />
      );
    case 'time':
      return (
        <TimePicker
          value={value}
          onChange={date => {
            onChange({ target: { value: date, id } });
          }}
          placeholder={placeholder}
          onBlur={onBlur}
        />
      );
    case 'date':
      return (
        <DatePicker
          value={value}
          onChange={date => {
            onChange({ target: { value: date, id } });
          }}
          placeholder={placeholder}
          onBlur={onBlur}
          minDate={min}
        />
      );
    case 'currency':
      return (
        <Currency
          displayType="input"
          value={value}
          onChange={event => {
            onChange({ ...event, target: { ...event.target, id } });
          }}
          placeholder={placeholder}
          className="form-field__input"
          disabled={disabled}
          onBlur={onBlur}
        />
      );
    case 'select':
      return (
        <Select
          options={options}
          selected={options?.find(option => option.value === value)}
          onSelect={selected =>
            onChange({ target: { id, value: selected.value } })
          }
          onBlur={event => {
            onBlur({ ...event, target: { id } });
          }}
          id={id}
          name={name}
          disabled={disabled}
        />
      );
    case 'radio':
      return (
        <RadioButton
          options={options}
          selected={options.find(option => option.value === value)}
          onSelect={selected =>
            onChange({ target: { id, value: selected.value } })
          }
          onBlur={onBlur}
          url={url}
          orientation={orientation}
        />
      );
    case 'textarea':
      return (
        <textarea
          className="form-field__input"
          id={id}
          name={name}
          type={type}
          onChange={onChange}
          value={value}
          placeholder={placeholder}
          rows="7"
          onBlur={onBlur}
        >
          {value}
        </textarea>
      );
    
    default:
      return (
        <input
          className="form-field__input"
          id={id}
          name={name}
          type={type}
          onChange={onChange}
          value={value}
          placeholder={placeholder}
          onBlur={onBlur}
          pattern={pattern}
        />
      );
  }
};

const FormField = memo(
  ({
    label,
    id,
    name,
    type,
    onChange,
    onBlur,
    value,
    touched,
    error,
    required,
    options,
    placeholder,
    disabled,
    format,
    mask,
    customLabel,
    min,
    isLoading,
    pattern,
    url,
    orientation,
    inlineTitle,
    style,
  }) => {
    return (
      <div style={style} className={bem('form-field', {
        'inline-title': inlineTitle
      })}>
        <label className="form-field__label" htmlFor={id || name}>
          {customLabel || `${label} ${required ? '*' : ''}`}
        </label>
        {renderControl(
          id,
          name,
          type,
          onChange,
          value,
          options,
          placeholder,
          disabled,
          format,
          mask,
          onBlur,
          min,
          isLoading,
          pattern,
          url,
          orientation,
          inlineTitle
        )}
        {touched && error ? <ErrorLabel error={error} /> : null}
      </div>
    );
  }
);

export default FormField;
